import { Outlet, Link, useLocation } from "react-router-dom";
import {
  Drawer, List, ListItem, ListItemIcon, ListItemText, Box, Typography, IconButton, Divider, useMediaQuery
} from "@mui/material";
import { useContext, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { ColorModeContext } from "../ThemeContext";
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DashboardIcon from "@mui/icons-material/Dashboard";
import HistoryIcon from "@mui/icons-material/History";
import WarningIcon from "@mui/icons-material/Warning";
import AssessmentIcon from "@mui/icons-material/Assessment";
import BarChartIcon from "@mui/icons-material/BarChart";
import PeopleIcon from "@mui/icons-material/People";
import HelpIcon from "@mui/icons-material/Help";
import PsychologyIcon from "@mui/icons-material/Psychology";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";

const drawerWidth = 220;
const collapsedWidth = 60;

const menu = [
  { label: "Dashboard", path: "/dashboard", icon: <DashboardIcon /> },
  { label: "Histórico", path: "/historico", icon: <HistoryIcon /> },
  { label: "Ocorrências", path: "/alarmes", icon: <WarningIcon /> },
  { label: "Relatórios", path: "/relatorios", icon: <AssessmentIcon /> },
  { label: "Estatísticas", path: "/estatisticas", icon: <BarChartIcon /> },
  { label: "Análise IA", path: "/ia", icon: <PsychologyIcon /> },
  { label: "Usuários", path: "/usuarios", icon: <PeopleIcon /> },
  { label: "Assistência", path: "/assistencia", icon: <HelpIcon /> },
];

export default function MainLayout() {
  const location = useLocation();
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);

  const isLogged = !!localStorage.getItem("token");

  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);
  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/login";
  };

  const drawerContent = (
    <Box>
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: drawerOpen ? "space-between" : "center",
        px: drawerOpen ? 2 : 0,
        py: 2
      }}>
        {drawerOpen && (
          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
            {/* Moldura branca fixa para a logo */}
            <Box
              sx={{
                backgroundColor: '#fff',
                borderRadius: 2,
                p: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 54,
                maxWidth: 180,
                mr: 1,
                ml: -0.5,
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 0 0 1px rgba(0,0,0,0.2), 0 2px 6px rgba(0,0,0,0.25)'
                  : '0 0 0 1px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.12)',
              }}
            >
              {/* === SUA LOGO DO PUBLIC ===
                  Coloque o arquivo em /public/iseq-logo.png */}
              <Box
                component="img"
                src="/iseq-logo.png"
                alt="iSEQ • Sensores & Instrumentos"
                sx={{
                  height: 56,
                  width: 'auto',
                  display: 'block',
                  objectFit: 'contain',
                }}
              />
            </Box>
          </Box>
        )}
        {!isMobile && (
          drawerOpen ? (
            <IconButton
              onClick={() => setDrawerOpen(false)}
              size="small"
              color="primary"
              sx={{
                ml: 1,
                bgcolor: (theme) => theme.palette.mode === 'dark' ? '#222' : '#f5f5f5',
                border: '1px solid',
                borderColor: (theme) => theme.palette.divider,
                '&:hover': { bgcolor: (theme) => theme.palette.action.hover },
                boxShadow: 2,
              }}
            >
              <ChevronLeftIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
            </IconButton>
          ) : (
            <IconButton
              onClick={() => setDrawerOpen(true)}
              size="small"
              color="primary"
              sx={{
                bgcolor: (theme) => theme.palette.mode === 'dark' ? '#222' : '#f5f5f5',
                border: '1px solid',
                borderColor: (theme) => theme.palette.divider,
                '&:hover': { bgcolor: (theme) => theme.palette.action.hover },
                boxShadow: 2,
              }}
            >
              <MenuIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
            </IconButton>
          )
        )}
      </Box>

      <Divider />

      <List>
        {menu.map((item) => (
          <ListItem
            key={item.label}
            component={Link}
            to={item.path}
            selected={location.pathname.startsWith(item.path)}
            onClick={() => isMobile && setDrawerOpen(false)}
            sx={{
              color: 'text.primary',
              textDecoration: 'none',
              '&:link, &:visited, &:hover, &:active': {
                color: 'text.primary',
                textDecoration: 'none',
              },
              flexDirection: drawerOpen ? "row" : "column",
              alignItems: "center",
              px: drawerOpen ? 2 : 0,
              py: 1.2,
              minHeight: 44,
              justifyContent: drawerOpen ? "initial" : "center",
              '& .MuiListItemIcon-root': { color: 'text.primary' },
              '& .MuiListItemText-primary': { color: 'text.primary' },
              '&:hover': { bgcolor: theme.palette.action.hover },
              '&.Mui-selected, &.Mui-selected:hover': {
                bgcolor: theme.palette.action.selected,
                borderLeft: drawerOpen ? `4px solid ${theme.palette.primary.main}` : "none",
                fontWeight: 700,
                '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
                '& .MuiListItemText-primary': { color: 'text.primary' },
              },
            }}
          >
            <ListItemIcon sx={{
              minWidth: drawerOpen ? 36 : "auto",
              mb: drawerOpen ? 0 : 0.5,
            }}>
              {item.icon}
            </ListItemIcon>

            {drawerOpen && (
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ sx: { fontSize: 15, color: 'text.primary' } }}
              />
            )}
          </ListItem>
        ))}
      </List>

      {/* Botão login/logout */}
      <Divider sx={{ my: 2 }} />

      <List>
        {isLogged ? (
          <ListItem
            onClick={handleLogout}
            sx={{
              color: "#b71c1c",
              textDecoration: 'none',
              flexDirection: drawerOpen ? "row" : "column",
              alignItems: "center",
              px: drawerOpen ? 2 : 0,
              py: 1.2,
              minHeight: 44,
              justifyContent: drawerOpen ? "initial" : "center",
              fontWeight: 700,
              '&:hover': { bgcolor: "#ffeaea" },
              '& .MuiListItemIcon-root': { color: "#b71c1c" },
            }}
          >
            <ListItemIcon sx={{
              minWidth: drawerOpen ? 36 : "auto",
              mb: drawerOpen ? 0 : 0.5
            }}>
              <LogoutIcon />
            </ListItemIcon>
            {drawerOpen && <ListItemText primary="Sair" />}
          </ListItem>
        ) : (
          <ListItem
            component={Link}
            to="/login"
            sx={{
              color: 'text.primary',
              textDecoration: 'none',
              '&:link, &:visited, &:hover, &:active': {
                color: 'text.primary',
                textDecoration: 'none',
              },
              flexDirection: drawerOpen ? "row" : "column",
              alignItems: "center",
              px: drawerOpen ? 2 : 0,
              py: 1.2,
              minHeight: 44,
              justifyContent: drawerOpen ? "initial" : "center",
              fontWeight: 700,
              '&:hover': { bgcolor: theme.palette.action.hover },
              '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
            }}
          >
            <ListItemIcon sx={{
              minWidth: drawerOpen ? 36 : "auto",
              mb: drawerOpen ? 0 : 0.5
            }}>
              <LoginIcon />
            </ListItemIcon>
            {drawerOpen && <ListItemText primary="Entrar" />}
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", maxWidth: "100vw", overflowX: "hidden" }}>
      {/* Drawer responsivo */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isMobile ? drawerWidth : (drawerOpen ? drawerWidth : collapsedWidth),
          flexShrink: 0,
          whiteSpace: "nowrap",
          [`& .MuiDrawer-paper`]: {
            width: isMobile ? drawerWidth : (drawerOpen ? drawerWidth : collapsedWidth),
            transition: "width 0.2s",
            overflowX: "hidden",
            boxSizing: "border-box",
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderRight: "1px solid " + theme.palette.divider,
            zIndex: isMobile ? 2001 : "auto",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Botão menu para mobile */}
      {isMobile && !drawerOpen && (
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed', top: 10, left: 10, zIndex: 2100,
            bgcolor: "background.paper",
            border: '1px solid',
            borderColor: theme.palette.divider
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Conteúdo principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: theme.palette.background.default,
          color: theme.palette.text.primary,
          minHeight: "100vh",
          width: "100vw",
          overflowX: "hidden",
          p: { xs: 1, md: 3 },
          transition: "padding 0.2s",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minHeight: 56,
            mb: 2,
            justifyContent: "space-between"
          }}
        >
          <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ fontSize: { xs: 18, md: 28 } }}>
            {menu.find(m => location.pathname.startsWith(m.path))?.label
              || (location.pathname === "/ia" ? "Previsão Inteligente (IA)" : "")
            }
          </Typography>
          <IconButton onClick={colorMode.toggleColorMode} color="inherit">
            {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        </Box>

        <Outlet />
      </Box>
    </Box>
  );
}

